import { Eye, RotateCcw } from "lucide-react";
import { PlayerCard } from "@/components/poker/player-card";
import { Button } from "@/components/ui/button";
import type { Player } from "@/lib/api/rooms";

export function PokerTable({
  players,
  votes,
  revealed,
  myPlayerId,
  canReveal,
  revealing,
  onReveal,
  onNewRound,
  onInvite,
}: {
  players: Player[];
  /** Card per player id. */
  votes: Map<string, string>;
  revealed: boolean;
  myPlayerId: string;
  canReveal: boolean;
  /** A reveal or new round request is in flight. */
  revealing: boolean;
  onReveal: () => void;
  onNewRound: () => void;
  onInvite: () => void;
}) {
  const someVoted = players.some((player) => player.has_voted);
  const allVoted = players.length > 0 && players.every((player) => player.has_voted);

  const seat = (player: Player) => (
    <PlayerCard
      key={player.id}
      name={player.name}
      hasVoted={player.has_voted}
      vote={votes.get(player.id)}
      revealed={revealed}
      isMe={player.id === myPlayerId}
      away={!player.active}
    />
  );

  return (
    <div className="flex w-full max-w-3xl flex-col items-center gap-6" data-testid="poker-table">
      <div className="flex flex-wrap justify-center gap-4">
        {players.filter((_, index) => index % 2 === 0).map(seat)}
      </div>

      <div className="relative flex min-h-[140px] w-full max-w-xl items-center justify-center rounded-2xl border border-border/50 bg-secondary/40 px-8 py-10">
        {!revealed ? (
          <div className="text-center">
            {!someVoted && <p className="text-lg text-muted-foreground">Pick your cards! 👇</p>}
            {someVoted && !allVoted && (
              <p className="text-lg text-muted-foreground">Waiting for votes...</p>
            )}
            {someVoted && canReveal && (
              <Button onClick={onReveal} className="mt-3" size="lg" disabled={revealing}>
                <Eye />
                Reveal Cards
              </Button>
            )}
          </div>
        ) : (
          <div className="text-center">
            <p className="mb-3 text-lg font-semibold">Votes revealed! 🎉</p>
            {canReveal && (
              <Button onClick={onNewRound} variant="outline" disabled={revealing}>
                <RotateCcw />
                New Round
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap justify-center gap-4">
        {players.filter((_, index) => index % 2 === 1).map(seat)}
      </div>

      {players.length <= 1 && (
        <p className="text-sm text-muted-foreground">
          Feeling lonely? 🥺{" "}
          <button
            type="button"
            onClick={onInvite}
            className="cursor-pointer text-primary underline hover:no-underline"
          >
            Invite some players!
          </button>
        </p>
      )}
    </div>
  );
}
